import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, UsersThree, Medal } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { fmtDate } from "@/lib/api";
import { PageHero, Section, Loader } from "@/components/Blocks";

const RANK_TONE = ["#E0A21B", "#94A3B8", "#B4693A"];

const Board = ({ board }) => (
  <div className="panel overflow-hidden" data-testid={`board-${board.city_slug}`}>
    <div className="flex items-center justify-between bg-[#0B1B33] px-5 py-4 text-white">
      <h3 className="font-display text-2xl font-bold uppercase tracking-tight">{board.city_name}</h3>
      <span className="text-xs uppercase tracking-widest text-white/60">Top {board.entries.length}</span>
    </div>
    <ol className="divide-y">
      {board.entries.map((e) => (
        <li key={e.rank} className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-[#F4F6FA]" data-testid={`board-entry-${board.city_slug}-${e.rank}`}>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full font-mono-num text-sm font-bold text-white" style={{ background: RANK_TONE[e.rank - 1] || "#0B1B33" }}>{e.rank}</span>
          <span className="flex-1 font-semibold text-[#0B1B33]">{e.display}</span>
          <span className="flex items-center gap-1.5 text-sm text-[#475569]"><UsersThree size={18} /><span className="font-mono-num font-semibold text-[#0B1B33]">{e.count}</span> attended</span>
        </li>
      ))}
    </ol>
  </div>
);

export default function Leaderboard() {
  const { data, loading, reload } = useFetch("/leaderboard");
  const [city, setCity] = useState("");
  useEffect(() => {
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);
  if (loading && !data) return <Loader />;
  const boards = data?.boards || [];
  const shown = city ? boards.filter((b) => b.city_slug === city) : boards;
  return (
    <>
      <PageHero eyebrow="Crew builders" title="Referral leaderboard"
        sub={data?.season ? `${data.season.name} · ${fmtDate(data.season.starts_at)} to ${fmtDate(data.season.ends_at)}. Ranked by friends who checked in at class.` : "The next season has not started yet."} />
      <Section>
        <div className="mb-8 grid gap-4 md:grid-cols-3" data-testid="leaderboard-how">
          {[[Trophy, "Opt-in only", "Students choose to appear and can leave any time."], [UsersThree, "Friends who attended", "Only referred friends who checked in at class count."], [Medal, "Recognition", "Leaders earn the Top Crew Builder badge, an on-stage shout-out or a VIP seat upgrade."]].map(([Icon, t, s]) => (
            <div key={t} className="flex gap-3"><Icon size={28} weight="duotone" className="shrink-0 text-[#E0A21B]" /><div><div className="font-semibold text-[#0B1B33]">{t}</div><p className="text-sm text-[#475569]">{s}</p></div></div>
          ))}
        </div>
        {data?.contest && <p className="mb-6 text-sm" data-testid="contest-running">A contest is running this season. <Link to="/leaderboard/contest-rules" className="font-semibold underline" data-testid="contest-rules-link">Read the contest rules</Link>.</p>}
        {boards.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="leaderboard-city-filter">
            <button onClick={() => setCity("")} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${!city ? "border-[#0B1B33] bg-[#0B1B33] text-white" : "bg-white hover:border-[#0B1B33]"}`} data-testid="city-chip-all">All cities</button>
            {boards.map((b) => <button key={b.city_slug} onClick={() => setCity(b.city_slug)} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${city === b.city_slug ? "border-[#0B1B33] bg-[#0B1B33] text-white" : "bg-white hover:border-[#0B1B33]"}`} data-testid={`city-chip-${b.city_slug}`}>{b.city_name}</button>)}
          </div>
        )}
        {shown.length === 0 ? (
          <div className="panel p-10 text-center text-sm text-[#475569]" data-testid="leaderboard-empty">No one is on the board yet this season. Students can join from the Bring a friend page in their student app.</div>
        ) : <div className="grid items-start gap-6 lg:grid-cols-2">{shown.map((b) => <Board key={b.city_id} board={b} />)}</div>}
        <p className="mt-8 text-xs text-[#475569]">Share only with people you know, using the approved messages in your student app. No profit or income claims.</p>
      </Section>
    </>
  );
}

export function ContestRules() {
  const { data, error, loading } = useFetch("/leaderboard/contest-rules");
  if (loading) return <Loader />;
  return (
    <>
      <PageHero eyebrow="Leaderboard" title="Contest rules" sub={data ? `Approved ${fmtDate(data.approved_at)}` : ""} />
      <Section tone="white"><div className="max-w-3xl whitespace-pre-line text-[#334155]" data-testid="contest-rules-body">{error ? "No contest is running right now." : data.rules}</div></Section>
    </>
  );
}
